import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, RotateCcw, X, Trash2 } from 'lucide-react';
import { listAccounts, listJournals, createJournal, reverseJournal, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const emptyLine = () => ({ account_id: '', description: '', debit: 0, credit: 0 });

const JournalsPage = () => {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    reference: '', narration: '',
    lines: [emptyLine(), emptyLine()],
  });

  const load = () => {
    setLoading(true);
    Promise.all([listJournals(), listAccounts()])
      .then(([j, a]) => { setItems(j.data || []); setAccounts(a.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const acctMap = useMemo(() => {
    const m = {}; accounts.forEach((a) => { m[a.account_id] = a; }); return m;
  }, [accounts]);

  const totals = useMemo(() => {
    const d = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const c = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    return { d, c, balanced: Math.abs(d - c) < 0.005 && d > 0 };
  }, [form.lines]);

  const setLine = (i, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    try {
      await createJournal({
        ...form,
        lines: form.lines.map((l) => ({
          ...l,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      });
      toast({ title: 'Journal posted' });
      setOpenNew(false);
      setForm({ date: new Date().toISOString().slice(0, 10), reference: '', narration: '', lines: [emptyLine(), emptyLine()] });
      load();
    } catch (e) {
      toast({ title: errMsg(e), variant: 'destructive' });
    }
  };

  const reverse = async (id) => {
    if (!window.confirm('Post a reversing journal? The original remains intact.')) return;
    try {
      await reverseJournal(id);
      toast({ title: 'Reversal posted' });
      load();
    } catch (e) {
      toast({ title: errMsg(e), variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Ledger</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Journals</h1>
        </div>
        <button onClick={() => setOpenNew(true)} data-testid="new-journal-btn"
          className="inline-flex items-center gap-1 px-4 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">
          <Plus className="w-4 h-4" /> New Journal
        </button>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
      items.length === 0 ? (
        <div className="p-12 text-center text-[#0b1a2e]/40 italic bg-white rounded-2xl border border-black/5">No journals yet — post your first one.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
              <tr>
                <th className="text-left px-4 py-2">Journal</th>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Narration</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-right px-4 py-2 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((j) => (
                <React.Fragment key={j.journal_id}>
                  <tr className={`border-t border-slate-100 ${j.is_reversal_of ? 'bg-red-50/40' : ''}`}>
                    <td className="px-4 py-2 font-mono text-xs">
                      {j.journal_no}
                      {j.is_reversal_of && <span className="ml-1 text-[10px] uppercase tracking-widest text-red-700">rev.</span>}
                      {j.reversed_by_journal_id && <span className="ml-1 text-[10px] uppercase tracking-widest text-red-700 line-through">reversed</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-[#0b1a2e]/70">{j.date}</td>
                    <td className="px-4 py-2 text-[#0b1a2e]">{j.narration || j.reference}</td>
                    <td className="px-4 py-2 text-right font-mono">{Number(j.total_debit).toFixed(2)}</td>
                    <td className="px-4 py-2 text-right">
                      {!j.reversed_by_journal_id && !j.is_reversal_of && (
                        <button onClick={() => reverse(j.journal_id)} title="Reverse this entry"
                          className="text-red-600 hover:text-red-700" data-testid={`reverse-${j.journal_id}`}>
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                  <tr className="text-xs">
                    <td colSpan="5" className="px-4 py-1 pb-2 text-[#0b1a2e]/70">
                      <div className="ml-4 space-y-0.5">
                        {j.lines.map((l) => (
                          <div key={l.line_id} className="flex gap-3">
                            <span className="w-16 font-mono">{acctMap[l.account_id]?.code || '?'}</span>
                            <span className="flex-1">{acctMap[l.account_id]?.name || l.account_id}</span>
                            <span className="w-24 text-right font-mono">{l.debit ? Number(l.debit).toFixed(2) : ''}</span>
                            <span className="w-24 text-right font-mono">{l.credit ? Number(l.credit).toFixed(2) : ''}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-medium text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Post journal entry</h2>
              <button onClick={() => setOpenNew(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <F label="Date"><input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="jrnl-date" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
              <F label="Reference"><input value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
              <F label="Narration"><input value={form.narration} onChange={(e) => setForm((f) => ({ ...f, narration: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
            </div>
            <table className="w-full text-sm mb-3">
              <thead className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                <tr>
                  <th className="text-left py-1">Account</th>
                  <th className="text-left py-1">Description</th>
                  <th className="text-right py-1 w-28">Debit</th>
                  <th className="text-right py-1 w-28">Credit</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1 pr-2">
                      <select value={l.account_id} onChange={(e) => setLine(i, { account_id: e.target.value })}
                        data-testid={`jrnl-line-${i}-account`}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs">
                        <option value="">Select…</option>
                        {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}
                      </select>
                    </td>
                    <td className="py-1 pr-2"><input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" /></td>
                    <td className="py-1 pr-2"><input type="number" step="0.01" value={l.debit || ''} onChange={(e) => setLine(i, { debit: e.target.value, credit: 0 })} data-testid={`jrnl-line-${i}-debit`} className="w-full px-2 py-1 border border-slate-300 rounded text-xs text-right font-mono" /></td>
                    <td className="py-1 pr-2"><input type="number" step="0.01" value={l.credit || ''} onChange={(e) => setLine(i, { credit: e.target.value, debit: 0 })} data-testid={`jrnl-line-${i}-credit`} className="w-full px-2 py-1 border border-slate-300 rounded text-xs text-right font-mono" /></td>
                    <td className="py-1">{form.lines.length > 2 && <button onClick={() => rmLine(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="text-xs">
                <tr className="border-t border-slate-200">
                  <td colSpan="2" className="py-2 text-right text-[#0b1a2e]/60">Totals</td>
                  <td className="text-right font-mono">{totals.d.toFixed(2)}</td>
                  <td className="text-right font-mono">{totals.c.toFixed(2)}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="5" className={`text-right py-1 text-xs ${totals.balanced ? 'text-[#0f5c3d]' : 'text-red-600'}`} data-testid="jrnl-balanced-hint">
                    {totals.balanced ? '✓ Balanced' : `Difference: ${(totals.d - totals.c).toFixed(2)}`}
                  </td>
                </tr>
              </tfoot>
            </table>
            <button onClick={addLine} className="text-xs text-[#0f5c3d] font-semibold">+ Add line</button>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpenNew(false)} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
              <button onClick={submit} disabled={!totals.balanced} data-testid="jrnl-post"
                className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">
                Post journal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default JournalsPage;
